// teamsSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  TeamsMembersResponse,
  TeamsMembersState,
  TeamsMember,
  ApiError,
} from "./teamsMemberTypes";
import {
  deleteTeamMember,
  deleteTeamMemberInvitation,
  fetchBaseTeamMemberInvitationStatus,
  fetchTeamsMembersData,
  updateTeamMember,
} from "../../services/teamMemberService";

export const fetchTeamsMemberList = createAsyncThunk<
  TeamsMembersResponse,
  { teamId: number; role: string },
  { rejectValue: string }
>("base/fetchTeamsMembersList", async ({ teamId, role }, { rejectWithValue }) => {
  try {
    if (teamId === undefined) {
      throw new Error("team members ID is undefined");
    }
    const response = await fetchTeamsMembersData(teamId,role);
    return response;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue(error.message || "Failed to fetch base data");
  }
});

// base team member invitation status

export const baseTeamMemberInvitationStatusData = createAsyncThunk<
  TeamsMembersResponse,
  { baseId: number; teamId: number },
  { rejectValue: string }
>(
  "base/fetchTeamMemberInvitationStatus",
  async ({ baseId, teamId }, { rejectWithValue }) => {
    try {
      if (baseId === undefined || teamId === undefined) {
        throw new Error("Base ID or User ID is undefined");
      }
      const response = await fetchBaseTeamMemberInvitationStatus(
        baseId,
        teamId,
      );
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || "Failed to fetch base data");
    }
  },
);

// base member invitation remove
export const baseTeamMemberRemove = createAsyncThunk<
  TeamsMembersResponse,
  { baseId: number; invitationId: number },
  { rejectValue: string }
>(
  "base/baseMemberRemove",
  async ({ baseId, invitationId }, { rejectWithValue }) => {
    try {
      const response = await deleteTeamMemberInvitation(baseId, invitationId);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove member",
      );
    }
  },
);

export const updateTeamMemberDetails = createAsyncThunk(
  "base/updateTeamMemberDetails",
  async (
    { baseId, team_id, data }: { baseId: number; team_id: number; data: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateTeamMember(baseId, team_id, data);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to update member",
      );
    }
  },
);

export const deleteTeamMemberDetails = createAsyncThunk(
  "base/deleteTeamMemberDetails",
  async ({ team_id }: { team_id: number }, { rejectWithValue }) => {
    try {
      const response = await deleteTeamMember(team_id);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete member",
      );
    }
  },
);

const initialState: TeamsMembersState = {
  TeamsMemberDetails: null,
  TeamsMemberStatus: "idle",
  TeamsMemberStatusError: null,
  baseInvitationStatusDetails: null,
  baseInvitationStatus: "idle",
  baseInvitationStatusError: null,
  baseMemberRemoveStatus: "idle",
  baseMemberRemoveError: null,
  baseMemberRemoveData: null,
  baseMemberUpdateData: null,
  baseMemberUpdateStatus: "idle",
  baseMemberUpdateError: null,
  // delete
  deleteTeamMemberStatus: "idle",
  deleteTeamMemberError: null,
  deleteTeamMemberData: null,
};

const teamsSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamsMemberList.pending, (state) => {
        state.TeamsMemberStatus = "loading";
      })
      .addCase(
        fetchTeamsMemberList.fulfilled,
        (state, action: PayloadAction<TeamsMembersResponse>) => {
          state.TeamsMemberStatus = "succeeded";
          state.TeamsMemberDetails = action.payload.data;
          localStorage.setItem("baseTeamsData", JSON.stringify(action.payload));
        },
      )
      .addCase(fetchTeamsMemberList.rejected, (state, action) => {
        state.TeamsMemberStatus = "failed";
        state.TeamsMemberStatusError =
          action.payload || "Failed to fetch boards";
      })
      .addCase(baseTeamMemberInvitationStatusData.pending, (state) => {
        state.baseInvitationStatus = "loading";
      })
      .addCase(
        baseTeamMemberInvitationStatusData.fulfilled,
        (state, action: PayloadAction<TeamsMembersResponse>) => {
          state.baseInvitationStatus = "succeeded";
          state.baseInvitationStatusDetails = action.payload.data;
        },
      )
      .addCase(baseTeamMemberInvitationStatusData.rejected, (state, action) => {
        state.baseInvitationStatus = "failed";
        state.baseInvitationStatusError =
          action.payload || "Failed to update base";
      })
      .addCase(baseTeamMemberRemove.pending, (state) => {
        state.baseMemberRemoveStatus = "loading";
      })
      .addCase(
        baseTeamMemberRemove.fulfilled,
        (state, action: PayloadAction<TeamsMembersResponse>) => {
          state.baseMemberRemoveStatus = "succeeded";
          state.baseMemberRemoveData = action.payload;
        },
      )
      .addCase(baseTeamMemberRemove.rejected, (state, action) => {
        state.baseMemberRemoveStatus = "failed";
        state.baseMemberRemoveError = action.payload || "Failed to update base";
      })
      .addCase(updateTeamMemberDetails.pending, (state) => {
        state.baseMemberUpdateStatus = "loading";
      })
      .addCase(
        updateTeamMemberDetails.fulfilled,
        (state, action: PayloadAction<TeamsMembersResponse>) => {
          state.baseMemberUpdateStatus = "succeeded";
          state.baseMemberUpdateData = action.payload.data;
        },
      )
      .addCase(updateTeamMemberDetails.rejected, (state, action) => {
        state.baseMemberUpdateStatus = "failed";
        state.baseMemberUpdateError =
          (action.payload as string) || "Failed to update base";
      })
      .addCase(deleteTeamMemberDetails.pending, (state) => {
        state.deleteTeamMemberStatus = "loading";
      })
      .addCase(
        deleteTeamMemberDetails.fulfilled,
        (state, action: PayloadAction<TeamsMembersResponse>) => {
          state.deleteTeamMemberStatus = "succeeded";
          state.deleteTeamMemberData = action.payload.data;
        },
      )
      .addCase(deleteTeamMemberDetails.rejected, (state, action) => {
        state.deleteTeamMemberStatus = "failed";
        state.deleteTeamMemberError =
          (action.payload as string) || "Failed to update base";
      });
  },
});

export default teamsSlice.reducer;
